// Generic / noisy backend messages that aren't useful to the user and should
// never be shown in a toast or notification. Keep entries lowercase; matching
// is case-insensitive and trims surrounding whitespace and trailing periods.
export const suppressedMessages = [
  "validation error",
  "an unexpected error occurred",
];

const normalize = (message?: unknown): string =>
  String(message ?? "")
    .trim()
    .replace(/\.+$/, "")
    .toLowerCase();

/**
 * Returns true when a message is empty or matches a suppressed message and
 * therefore should NOT be surfaced to the user.
 */
export const isSuppressedMessage = (message?: unknown): boolean => {
  const normalized = normalize(message);
  return normalized === "" || suppressedMessages.includes(normalized);
};

// The app runs two independent toast systems (antd `notification` fired by the
// axios interceptor + sonner `toast` fired inside page catch blocks). The same
// error therefore tends to be shown twice, overlapping in the top-right corner.
// We de-duplicate by remembering recently shown messages for a short window.
const DEDUPE_WINDOW_MS = 2500;
const recentMessages = new Map<string, number>();

/**
 * Central gate for every user-facing toast/notification. Returns true when the
 * message should be hidden because it is suppressed (generic) or because the
 * exact same message was just shown by the other toast system.
 */
export const shouldSuppressToast = (message?: unknown): boolean => {
  if (isSuppressedMessage(message)) {
    return true;
  }

  const key = normalize(message);
  const now = Date.now();

  for (const [seen, at] of recentMessages) {
    if (now - at > DEDUPE_WINDOW_MS) {
      recentMessages.delete(seen);
    }
  }

  if (recentMessages.has(key)) {
    return true;
  }

  recentMessages.set(key, now);
  return false;
};
